import * as React from 'react';
import { getBaseUrl } from '@/redux/modules/config';
import { getDiscoverPageUrl, getItemCardImageUrl, logoUrl } from '@/utils/urls';
import { getDiscoverPaginationFilter, getShouldDiscoverTopic } from '@/redux/modules/discover';
import { getItemImportantCategories } from '@/redux/modules/searchFacetAggregates.selectors';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useParams } from 'react-router-dom';
import capitalize from '@liveauctioneers/caterwaul-components/lib/utils/capitalize';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

type Props = {
    firstItemId: number;
    title: string;
};

type RouteParams = {
    topic: string;
};

const DiscoverPageHeaderMeta = ({ firstItemId, title }: Props) => {
    const { formatMessage, formatNumber } = useIntl();
    const baseUrl = useAppSelector(getBaseUrl);
    const { topic } = useParams<RouteParams>();
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, firstItemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, firstItemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, firstItemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, firstItemId));
    const categories = useAppSelector(getItemImportantCategories);
    const { totalRecords } = useAppSelector(getDiscoverPaginationFilter);
    const formattedTotalRecords = formatNumber(totalRecords);
    let capitalizedTitle = capitalize(title);
    const shouldDiscover = useAppSelector((state) => getShouldDiscoverTopic(state, title));
    const firstPhoto = photos?.[0] || 0;
    const imageUrl = getItemCardImageUrl(sellerId, catalogId, firstItemId, firstPhoto, imageVersion) || logoUrl;
    const canonical = `${baseUrl}${getDiscoverPageUrl(topic)}`;
    const [l1First, l1Second] = categories;
    const description =
        categories.length > 1
            ? formatMessage(
                  { id: 'discoverPage.meta.descriptionMultipleCategories' },
                  {
                      l1First: l1First?.name || '',
                      l1Second: l1Second?.name || '',
                      term: capitalizedTitle,
                      totalRecords: formattedTotalRecords,
                  }
              )
            : formatMessage(
                  { id: 'discoverPage.meta.description' },
                  {
                      l1First: l1First?.name,
                      term: capitalizedTitle,
                      totalRecords: formattedTotalRecords,
                  }
              );
    capitalizedTitle = useAddNotificationCountToTitle(capitalizedTitle);

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={capitalizedTitle}
        >
            {!shouldDiscover && (
                <meta
                    content="noindex, nofollow"
                    name="robots"
                />
            )}
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(DiscoverPageHeaderMeta);
